import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Form, Select } from '@arco-design/web-react';

import { getTenantListAndVal } from '@/layout/servies';
import { getBindingworkspaces } from '@/pages/clusters/service';

import styles from './index.module.less';

const Option = Select.Option;

interface WorkspaceOption {
  name: string;
  /** 后端返回的租户别名，租户选择器统一使用此字段展示。 */
  displayName?: string;
  namespace?: string;
}
type WorkspaceChangeHandler = (v: string, option?: WorkspaceOption, isInit?: boolean) => void;

const descriptionStyle = { fontSize: 12, color: 'rgba(0,0,0,0.4)', marginLeft: 10 };

function findWorkspaceOption(options: WorkspaceOption[], name?: string) {
  return options.find((item) => item.name === name);
}

interface WorkspaceSelectFieldProps {
  hidden?: boolean;
  showLabel?: boolean;
  field: string;
  rules?: any[];
  initialValue?: string;
  disabled?: boolean;
  options: WorkspaceOption[];
  value: string | undefined;
  onChange: WorkspaceChangeHandler;
  setValue: (v: string) => void;
  renderOptionExtra?: (option: WorkspaceOption) => ReactNode;
}

function WorkspaceSelectField({
  hidden = false,
  showLabel = true,
  field,
  rules = [],
  initialValue,
  disabled = false,
  options,
  value,
  onChange,
  setValue,
  renderOptionExtra,
}: WorkspaceSelectFieldProps) {
  const { t } = useTranslation('cluster');

  return (
    <Form.Item
      hidden={hidden}
      label={showLabel && t('common:tenant.space')}
      field={field}
      rules={rules}
      className={styles.formItem}
      initialValue={initialValue}
    >
      <Select
        showSearch
        placeholder={t('place-holder-workerspaces')}
        onChange={(v) => {
          setValue(v);
          onChange(v, findWorkspaceOption(options, v), false);
        }}
        value={value}
        disabled={disabled}
        allowClear
      >
        {options.map((option) => (
          <Option key={option.name} value={option.name}>
            {option.displayName}
            {renderOptionExtra?.(option)}
          </Option>
        ))}
      </Select>
    </Form.Item>
  );
}

/** 选项列表就绪后，同步 defaultValue 并触发初始化回调 */
function useInitDefaultValue(
  defaultValue: string | undefined,
  options: WorkspaceOption[],
  onChange: WorkspaceChangeHandler,
  setSelected: (v: string) => void,
) {
  useEffect(() => {
    if (!defaultValue || !options.length) return;
    setSelected(defaultValue);
    onChange(defaultValue, findWorkspaceOption(options, defaultValue), true);
  }, [defaultValue, options]);
}

export default function WorkspaceItem({
  hidden = false,
  value = undefined,
  disabled = false,
  rules = [],
  field = '',
  showLabel = true,
  defaultValue = undefined,
  onChange = (v, option, isInit) => {},
}) {
  const params = useParams();
  const [workSpaces, setWorkSpaces] = useState<WorkspaceOption[]>([]);
  const [selected, setSelected] = useState(defaultValue);

  useInitDefaultValue(defaultValue, workSpaces, onChange, setSelected);

  useEffect(() => {
    getBindingworkspaces(params?.cluster)
      .then((data: any) => {
        if (data?.items) setWorkSpaces(data.items);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    setSelected(value);
  }, [value]);

  return (
    <WorkspaceSelectField
      hidden={hidden}
      showLabel={showLabel}
      field={field}
      rules={rules}
      initialValue={defaultValue}
      disabled={disabled}
      options={workSpaces}
      value={selected}
      onChange={onChange}
      setValue={setSelected}
      renderOptionExtra={(option) => <span style={descriptionStyle}>{option.name}</span>}
    />
  );
}

/** 全局租户选择器，使用 displayName 展示别名、name 作为选项值。 */
export function GlobalWorkspaceItem({
  disabled = false,
  rules = [],
  field = '',
  showLabel = true,
  defaultValue = undefined,
  hidden = false,
  onChange = (v, option, isInit) => {},
}) {
  const params = useParams();
  const [tenantList, setTenantList] = useState<WorkspaceOption[]>([]);
  const [selected, setSelected] = useState(defaultValue);

  useInitDefaultValue(defaultValue, tenantList, onChange, setSelected);

  useEffect(() => {
    getTenantListAndVal({ initialValue: defaultValue || params?.tenant })
      .then(({ data }) => {
        if (Array.isArray(data)) setTenantList(data);
      })
      .catch(() => {});
  }, []);

  return (
    <WorkspaceSelectField
      hidden={hidden}
      showLabel={showLabel}
      field={field}
      rules={rules}
      initialValue={defaultValue}
      disabled={disabled}
      options={tenantList}
      value={selected}
      onChange={onChange}
      setValue={setSelected}
      renderOptionExtra={(option) => <span style={descriptionStyle}>{option.name}</span>}
    />
  );
}
